import React, { useState, useMemo, useCallback } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUploadOptimized";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { ctagRetrievalFormFilters } from "../filterConfigurations";
import { formatDate } from "../../../utils/formatDate";

const PTPForms = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["PTP Forms"]?.edit;
  const {
    data: ctags,
    loading,
    error,
  } = useFirestore("CtagRetrievalForm", "Data_Status_Note", "asc");
  // Fetch PTP Status Notes for dropdown (force refresh on every render)
  const {
    data: ptpStatusNotesRaw,
    loading: ptpStatusNotesLoading,
    error: ptpStatusNotesError,
  } = useFirestore("PTPStatusNote", "statusNote", "asc");
  // Always create a new array reference to force re-render
  const ptpStatusNotes = useMemo(
    () => (ptpStatusNotesRaw ? [...ptpStatusNotesRaw] : []),
    [ptpStatusNotesRaw],
  );
  const [selectedCtag, setSelectedCtag] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "PTP Forms";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(ctags || [], ctagRetrievalFormFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        field: "Data_Status_Note",
        headerName: "PTP Status Note",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          return params.value || "";
        },
        filterable: true,
        type: "singleSelect",
        valueOptions: ptpStatusNotes
          ? ptpStatusNotes
              .map((n) => n.statusNote)
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b))
          : [],
      },
      { field: "Last_Name", headerName: "Last Name", flex: 1, minWidth: 120 },
      { field: "First_Name", headerName: "First Name", flex: 1, minWidth: 120 },
      {
        field: "DOB",
        headerName: "Date of Birth",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => formatDate(params.value),
      },
      { field: "Tartan_Id", headerName: "Tartan ID", flex: 1, minWidth: 120 },
      {
        field: "Sinclair_Email",
        headerName: "Sinclair Email",
        flex: 1,
        minWidth: 200,
      },
      { field: "HSGY", headerName: "HSGY", flex: 1, minWidth: 100 },
      {
        field: "created_at",
        headerName: "Create Date",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => formatDate(params.value),
      },
    ],
    [ptpStatusNotes],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((ctag, index) => ({
      id: ctag.id || index,
      ...ctag,
    }));
  }, [filteredData]);

  // Simple toolbar without search (search is external)
  const CustomToolbar = useCallback(() => {
    return (
      <SimpleToolbar
        exportOptions={{
          csvOptions: {
            fileName: "ptp-forms",
            delimiter: ",",
          },
          printOptions: {
            hideFooter: true,
            hideToolbar: true,
          },
        }}
      />
    );
  }, []);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCtag(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCtag(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  // Fields users can actually fill in on the blank "Export Template": the
  // list columns plus every field with its own editable input in
  // PTPForms/inc/Add.js (Edit.js mirrors the same set, with created_at
  // rendered read-only there since it's auto-generated on add).
  const templateFields = Array.from(
    new Set([
      ...columns.map((col) => col.field),
      "Personal_Email",
      "Ohio_SSID",
      "Current_Sinclair_Student",
      "Earned_CTAG",
      "CTAG_Site_of_Delivery",
      "Forms_Sent_From_CT_Institute",
      "CTAG_Instructor",
      "Phone_Number",
    ]),
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching PTP Forms:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={ctagRetrievalFormFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="PTP Forms Filters"
          resultCount={filteredData?.length || 0}
          totalCount={ctags?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && (
            <Add
              onRecordAdded={handleRecordAdded}
              ptpStatusNotes={ptpStatusNotes}
              ptpStatusNotesLoading={ptpStatusNotesLoading}
            />
          )}
          {canEdit && (
            <EditUpload
              collectionName="CtagRetrievalForm"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={ctags}
              templateFields={templateFields}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="CtagRetrievalForm" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: () => (
                <SimpleToolbar
                  exportColumnOrder={columns.map((col) => col.field)}
                  rows={rows}
                  onBulkActionComplete={handleBulkActionComplete}
                  pageName={pageName}
                />
              ),
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCtag && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          ctag={selectedCtag}
          onRecordUpdated={handleRecordUpdated}
          ptpStatusNotes={ptpStatusNotes}
          ptpStatusNotesLoading={ptpStatusNotesLoading}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PTPForms;
